import React, { memo, useCallback, useContext, useMemo, useEffect } from 'react';
import { AppContext } from '../contexts/appContext';
import LoaderContainer from './LoaderContainer';
import LoaderContextProvider from '../contexts/loaderContext';
import PurchaseContextProvider from '../contexts/purchaseContext';
import wizardSteps, { Pages } from '../constants/wizardSteps';
import { History } from 'history';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { LDContext } from 'launchdarkly-js-sdk-common';
import { LDPreQualContext } from '~/models/LDPreQualContext';
import { fingerprint } from '@sunbit-dev/common/services/fingerprint';

const EmailVerificationContainer = React.lazy(() => import('./EmailVerificationContainer'));
const PersonalDetailsContainer = React.lazy(() => import('./PersonalDetailsContainer'));
const PhoneVerificationContainer = React.lazy(() => import('./PhoneVerificationContainer'));
const ChooseYourPlanContainer = React.lazy(() => import('./ChooseYourPlanContainer'));
const PaymentDetailsContainer = React.lazy(() => import('./PaymentDetailsContainer'));
const ThankYouContainer = React.lazy(() => import('./ThankYouContainer'));

type MainProps = {
  history: History;
};

const MainContainer = memo(({ history }: MainProps) => {
  const {
    appData: { pageName: currentPageName },
    setNextPage,
    retailer,
  } = useContext(AppContext);

  const goToPage = useCallback(
    (nextPage: Pages) => {
      nextPage && setNextPage(nextPage);
    },
    [setNextPage]
  );

  const onNextPage = useCallback(() => {
    const { next } = wizardSteps[currentPageName];
    goToPage(next!);
  }, [currentPageName, goToPage]);

  const onPrevPage = useCallback(() => {
    const { prev } = wizardSteps[currentPageName] || {};
    prev && setNextPage(prev);
  }, [currentPageName, setNextPage]);

  const currentPage = useMemo(() => {
    switch (currentPageName) {
      case Pages.EnterPhoneNumber:
        return <PhoneVerificationContainer next={onNextPage} />;
      case Pages.CustomerDetails:
        return <PersonalDetailsContainer {...{ goToPage, history }} />;
      case Pages.EmailVerification:
        return <EmailVerificationContainer {...{ goToPage, history }} />;
      case Pages.ChooseYourPlan:
        return <ChooseYourPlanContainer {...{ next: onNextPage, prev: onPrevPage }} />;
      case Pages.PaymentDetails:
        return <PaymentDetailsContainer {...{ next: onNextPage }} />;
      case Pages.Thanks:
        return <ThankYouContainer />;
    }
  }, [currentPageName, onNextPage, goToPage, onPrevPage]);

  const ldClient = useLDClient();

  useEffect(() => {
    const updateLDContext = () => {
      const { visitorId } = fingerprint.getFingerprintResult();
      const newContext: LDPreQualContext = {
        kind: 'multi',
        user: {
          key: visitorId,
          id: visitorId,
        },
        retailer: {
          key: retailer.merchantId,
          name: retailer.merchantName,
          vertical: retailer.merchantVertical,
          id: retailer.merchantId,
        },
      };

      ldClient.identify(newContext as LDContext);
    };

    updateLDContext();
  }, []);

  return (
    <React.Suspense fallback={<LoaderContainer />}>
      <PurchaseContextProvider>
        <LoaderContextProvider>
          <LoaderContainer />
          {currentPage}
        </LoaderContextProvider>
      </PurchaseContextProvider>
    </React.Suspense>
  );
});

export default MainContainer;
